// Fuel: 101079eeb1
// Road safety: f40e09ae83
// Transport affordability: 93849cbc5a
// Environment: 8aa1dd4196
// Other: 8cd310697b

{
  /* <a href="https://aaa.asn.au/email-preferences/?id=*|LIST:UID|*&amp;e=*|UNIQID|*&amp;m=*|EMAIL|*" tabindex="-1"><span style="font-size: 11px">update your preferences</span></a> */
}

import { defineBlock } from "eddev/blocks"
import { Col, Container, Grid } from "@components/layout/Grid"
import { VSpace } from "@components/layout/Spacer"
import { cn, tv } from "@utils/tw"
import { Button } from "@components/atoms/Button"
import { ComponentPropsWithoutRef, forwardRef, useEffect, useRef, useState } from "react"
import { Heading } from "@components/atoms/Heading"
import { CARET_DOWN, TICK } from "@components/atoms/Icons"
import { useRoute } from "eddev/routing"
import { Typography } from "@components/atoms/Typography"

export const meta: BlockMeta = {
  title: "Update MailChimp Preferences",
  category: "common",
  icon: "email",
  keywords: ["MailChimp"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
    multiple: false,
  },
}

type Query = {
  e: string
  id: string
  m: string
}

type FormDetails = {
  firstName: string
  lastName: string
  emailAddress: string
  postCode: string
  state: string
  interests: {
    fuel: boolean
    roadSafety: boolean
    transportCosts: boolean
    environment: boolean
    other: boolean
  }
}

export default defineBlock("mailchimp/update-preferences", (props) => {
  const route = process.admin ? null : useRoute()
  const [query, setQuery] = useState<Query | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [details, setDetails] = useState<FormDetails | null>(null)
  const [complete, setComplete] = useState<boolean>(false)

  const fnameRef = useRef<HTMLInputElement>(null)
  const lnameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const postcodeRef = useRef<HTMLInputElement>(null)
  const stateRef = useRef<HTMLSelectElement>(null)

  const fuelRef = useRef<HTMLInputElement>(null)
  const roadSafetyRef = useRef<HTMLInputElement>(null)
  const transportCostsRef = useRef<HTMLInputElement>(null)
  const environmentRef = useRef<HTMLInputElement>(null)
  const otherRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (route?.query) {
      if (route.query?.e && route.query?.id && route.query?.m) {
        setQuery({
          e: route.query.e,
          id: route.query.id,
          m: route.query.m,
        })
      }
    }
  }, [route?.query])

  const getMemberInfo = (q: Query) => {
    fetch("/wp-json/mailchimp/get", {
      method: "POST",
      headers: {
        "content-type": "application/x-www-form-urlencoded",
      },
      body: `&e=${q.e}&id=${q.id}&m=${q.m}`,
    })
      .then((response) => response.json())
      .then((resp: any) => {
        if (resp.data.success) {
          setDetails({
            firstName: resp.data.data.merge_fields.FNAME || "",
            lastName: resp.data.data.merge_fields.LNAME || "",
            emailAddress: resp.data.data.email_address || "",
            postCode: resp.data.data.merge_fields.POSTCODE || "",
            state: resp.data.data.merge_fields.MMERGE5 || "",
            interests: {
              fuel: resp.data.data.interests["101079eeb1"],
              roadSafety: resp.data.data.interests["f40e09ae83"],
              transportCosts: resp.data.data.interests["93849cbc5a"],
              environment: resp.data.data.interests["8aa1dd4196"],
              other: resp.data.data.interests["8cd310697b"],
            },
          })
        }
        setLoading(false)
      })
  }

  const updateMemberInfo = (info: FormDetails, q: Query) => {
    fetch("/wp-json/mailchimp/update", {
      method: "POST",
      headers: {
        "content-type": "application/x-www-form-urlencoded",
      },
      body: JSON.stringify({
        e: q.e,
        id: q.id,
        m: q.m,
        info: info,
      }),
    })
      .then((response) => response.json())
      .then((resp: any) => {
        setComplete(true)
      })
  }

  useEffect(() => {
    if (query) {
      getMemberInfo(query)
    }
  }, [query])

  return (
    <PreferencesContainer>
      <Grid>
        <Col initial={12} tablet={1} laptop={2} />
        <Col initial={12} tablet={10} laptop={8}>
          <VSpace initial={8} />
          <Heading as="h2" variant="h2">
            Update your preferences
          </Heading>
          <VSpace initial={8} />
        </Col>

        {process.admin && (
          <Col initial={12}>
            <Warning>
              <Heading as="h4">Do not delete this block or page.</Heading>
              <Typography as="p" variant="text-m">
                The following code is what is needed to be replaced in the footer in the MailChimp templates if you
                would like to use this API page to update user preferences.
              </Typography>
              <code>
                &lt;a
                href="https://aaa.asn.au/email-preferences/?id=*|LIST:UID|*&amp;e=*|UNIQID|*&amp;m=*|EMAIL|*"&gt;&lt;span&gt;update
                your preferences&lt;/span&gt;&lt;/a&gt;
              </code>
            </Warning>
          </Col>
        )}

        <Col initial={12} tablet={1} laptop={2} />

        <Col initial={12} tablet={1} laptop={2} />
        <Col initial={12} tablet={10} laptop={8}>
          {query && !complete && (
            <Details
              onSubmit={(e) => {
                e.preventDefault()
                updateMemberInfo(
                  {
                    firstName: fnameRef.current?.value || "",
                    lastName: lnameRef.current?.value || "",
                    emailAddress: details?.emailAddress || "",
                    postCode: postcodeRef.current?.value || "",
                    state: stateRef.current?.value || "",
                    interests: {
                      fuel: fuelRef.current?.checked ? true : false,
                      roadSafety: roadSafetyRef.current?.checked ? true : false,
                      transportCosts: transportCostsRef.current?.checked ? true : false,
                      environment: environmentRef.current?.checked ? true : false,
                      other: otherRef.current?.checked ? true : false,
                    },
                  },
                  query,
                )
              }}
            >
              {loading ? (
                <LoadingError>
                  <Typography as="p" variant="h4">
                    Please wait, we are loading your profile.
                  </Typography>
                </LoadingError>
              ) : (
                <>
                  {details ? (
                    <>
                      <Heading as="h4" variant="h4">
                        Your details
                      </Heading>
                      <VSpace initial={6} />

                      <Fields>
                        <TextField>
                          <label>First Name*</label>
                          <input type="text" ref={fnameRef} defaultValue={details.firstName} />
                        </TextField>

                        <TextField>
                          <label>Last Name*</label>
                          <input type="text" ref={lnameRef} defaultValue={details.lastName} />
                        </TextField>

                        <TextField>
                          <label>Email*</label>
                          <input type="email" ref={emailRef} defaultValue={details.emailAddress} disabled />
                        </TextField>

                        <TextField>
                          <label>Postcode*</label>
                          <input type="text" ref={postcodeRef} defaultValue={details.postCode} />
                        </TextField>

                        <SelectField>
                          <label>State*</label>
                          <div className="relative w-full">
                            <select
                              ref={stateRef}
                              defaultValue={details.state}
                              className="w-full type-label-m py-4 px-3 rounded-[6px] border-none ring-1 ring-inset ring-mid-grey appearance-none outline-none focus:outline-none focus:ring-2 focus:ring-black"
                            >
                              <option value="">Please select</option>
                              <option value="NSW">NSW</option>
                              <option value="VIC">VIC</option>
                              <option value="QLD">QLD</option>
                              <option value="SA">SA</option>
                              <option value="WA">WA</option>
                              <option value="TAS">TAS</option>
                              <option value="NT">NT</option>
                              <option value="ACT">ACT</option>
                            </select>
                            <i className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-mid-grey [&_svg]:size-6">
                              {CARET_DOWN}
                            </i>
                          </div>
                        </SelectField>

                        <CheckboxGroup>
                          <label>What topics are you interested in?</label>

                          <Checkboxes>
                            <CheckboxField
                              label="Fuel related issues"
                              defaultValue={details.interests.fuel}
                              ref={fuelRef}
                            />
                            <CheckboxField
                              label="Road safety issues"
                              defaultValue={details.interests.roadSafety}
                              ref={roadSafetyRef}
                            />
                            <CheckboxField
                              label="Transport costs and affordability"
                              defaultValue={details.interests.transportCosts}
                              ref={transportCostsRef}
                            />
                            <CheckboxField
                              label="Environment related issues"
                              defaultValue={details.interests.environment}
                              ref={environmentRef}
                            />
                            <CheckboxField label="Other" defaultValue={details.interests.other} ref={otherRef} />
                          </Checkboxes>
                        </CheckboxGroup>
                      </Fields>

                      <footer>
                        <Button size="medium" color="yellow">
                          Update profile
                        </Button>
                      </footer>
                    </>
                  ) : (
                    <LoadingError>
                      <Typography as="p" variant="h4">
                        We are unable to load your profile at this time. Please ensure that you arrive at this page by
                        following the link in your email.
                      </Typography>
                    </LoadingError>
                  )}
                </>
              )}
            </Details>
          )}
          {complete && (
            <Complete>
              <Typography as="p" variant="h4">
                Thank you for updating you details.
              </Typography>
            </Complete>
          )}
          <VSpace initial={10} />
        </Col>
        <Col initial={12} tablet={1} laptop={2} />
      </Grid>
    </PreferencesContainer>
  )
})

const Warning = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col items-center border-2 border-red rounded-md bg-red-light text-red p-6 text-center",
      className,
    )}
    {...props}
  />
))
Warning.displayName = "Warning"

const PreferencesContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Container>>(
  ({ className, ...props }, ref) => (
    <Container ref={ref} className={cn("[&_h2]:block [&_h2]:text-center", className)} {...props} />
  ),
)
PreferencesContainer.displayName = "PreferencesContainer"

const LoadingError = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("w-full flex justify-center [&>p]:text-center", className)} {...props} />
))
LoadingError.displayName = "LoadingError"

const Complete = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col bg-light-grey rounded-md w-full p-7 [&>p]:w-full [&>p]:text-center", className)}
    {...props}
  />
))
Complete.displayName = "Complete"

const Details = forwardRef<HTMLFormElement, ComponentPropsWithoutRef<"form">>(({ className, ...props }, ref) => (
  <form
    ref={ref}
    className={cn(
      "flex flex-col bg-light-grey rounded-md w-full p-7",
      "[&_h4]:my-0",
      "[&_footer]:border-t [&_footer]:border-grey [&_footer]:mt-4 [&_footer]:pt-6 [&_footer]:flex [&_footer]:justify-end",
      className,
    )}
    {...props}
  />
))
Details.displayName = "Details"

const Fields = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-5", className)} {...props} />
))
Fields.displayName = "Fields"

const TextField = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col gap-1",
      "[&_label]:type-label-caps [&_label]:text-dark-grey",
      "[&_input]:type-label-m [&_input]:px-3 [&_input]:py-4 [&_input]:rounded-[6px] [&_input]:border-none",
      "[&_input]:ring-1 [&_input]:ring-inset [&_input]:ring-mid-grey [&_input]:outline-none",
      "[&_input:focus]:outline-none [&_input:focus]:ring-2 [&_input:focus]:ring-black",
      className,
    )}
    {...props}
  />
))
TextField.displayName = "TextField"

const SelectField = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-1 [&_label]:type-label-caps [&_label]:text-dark-grey", className)}
    {...props}
  />
))
SelectField.displayName = "SelectField"

const CheckboxGroup = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-1 [&_label]:type-title-xs [&_label]:text-black", className)}
    {...props}
  />
))
CheckboxGroup.displayName = "CheckboxGroup"

const Checkboxes = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col my-2 gap-2", className)} {...props} />
))
Checkboxes.displayName = "Checkboxes"

const indicatorStyles = tv({
  base: "inline-flex w-4 h-4 min-w-4 min-h-4 items-center justify-center overflow-hidden cursor-pointer mr-2 border border-dark-grey rounded-sm bg-white [&_svg]:w-full [&_svg]:h-full [&_svg]:scale-[1.6]",
  variants: {
    checked: {
      true: "bg-white",
    },
  },
})

const CheckboxField = forwardRef<HTMLInputElement, { label: string; defaultValue?: boolean }>(
  ({ label, defaultValue }: { label: string; defaultValue?: boolean }, ref) => {
    const [checked, setChecked] = useState<boolean>(false)

    useEffect(() => {
      if (defaultValue) setChecked(true)
    }, [defaultValue])

    return (
      <div className="flex items-center my-1 [&_label]:type-label-m [&_label]:cursor-pointer [&_label]:flex [&_label]:items-center [&_label_span]:type-label-m">
        <label>
          <input
            type="checkbox"
            className="hidden"
            aria-label={label}
            checked={checked}
            onChange={(e) => {
              setChecked(!checked)
            }}
            ref={ref}
          />
          <div className={indicatorStyles({ checked })}>{checked && <>{TICK}</>}</div>
          <span dangerouslySetInnerHTML={{ __html: label }} />
        </label>
      </div>
    )
  },
)
CheckboxField.displayName = "CheckboxField"
